import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { css, styled } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button, Refresh2Icon as RefreshIcon } from '@blade/ui-kit'
import { PairReused } from '@root/entities/pair/ui/pair-reused/pair-reused'
import { PairReusedSkeleton } from '@root/entities/pair/ui/pair-reused/pair-reused-skeleton'
import { PairPopover } from '@root/features/pair/ui'
import { FadeOverflow } from '@root/shared/ui-kit'

import { reatomReusedImages } from '../../atoms/reatom-reused-images'

import type { CurrencyAddress, ImageHash } from '@root/entities/currency/model'
import type { Network } from '@root/entities/network'
import type { TooltipCardProps } from '@root/shared/ui-kit'

type CurrencyIconPopoverDuplicatesProps = {
  imageHash: ImageHash | undefined
  network: Network.Network
  address: CurrencyAddress
  side?: TooltipCardProps['side']
}

const SKELETON_KEYS = [0] as const

export const CurrencyIconPopoverDuplicates = ({
  imageHash,
  network,
  address,
  side = 'bottom',
}: CurrencyIconPopoverDuplicatesProps) => {
  const { t } = useTranslation()

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule -- memoized request atom factory result
      reatomReusedImages({ imageHash, network, address }),
    [imageHash, network, address],
  )

  const [requestState] = useAtom(model)
  const refresh = useAction(model.refresh)

  return AsyncStateN.matchWaiting(requestState, {
    Waiting: () => (
      <DuplicatesContent>
        <SkeletonList>
          {SKELETON_KEYS.map((key) => (
            <PairReusedSkeleton key={key} />
          ))}
        </SkeletonList>
      </DuplicatesContent>
    ),
    Error: () => (
      <DuplicatesContent>
        <ErrorBlock>
          <RowStatus>{t('splash-points.error')}</RowStatus>
          <RetryButton
            variant="ghost"
            size="xs"
            radius="rounded"
            onClick={() => refresh()}
            iconLeft={<RefreshIcon />}
          >
            {t('common.retry')}
          </RetryButton>
        </ErrorBlock>
      </DuplicatesContent>
    ),
    Success: (pairs) =>
      pairs.length === 0 ? undefined : (
        <DuplicatesContent reusedImageCount={pairs.length}>
          <PairsFadeOverflow
            variant="vertical"
            blockStart={8}
            blockEnd={8}
            data-tooltip-scroll-lock=""
          >
            {pairs.map((pair) => (
              <PairPopoverContent
                layoutSize="auto"
                pair={pair}
                collisionPadding={8}
                key={pair.address}
                side={side}
              >
                <PairReused pair={pair} />
              </PairPopoverContent>
            ))}
          </PairsFadeOverflow>
        </DuplicatesContent>
      ),
  })
}

const PairPopoverContent = styled(PairPopover)`
  max-width: var(--radix-hover-card-content-available-width);
`

const DuplicatesContent = ({
  children,
  reusedImageCount,
}: {
  children: React.ReactNode
  reusedImageCount?: number
}) => {
  const { t } = useTranslation()
  return (
    <Root>
      <Title>
        {t('currency.duplicates.reused-icons')}{' '}
        {reusedImageCount ? `(${reusedImageCount})` : ''}
      </Title>
      {children}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 6px;
`

const Title = styled.p`
  color: var(--text-11);

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  margin: 0;
  padding-top: 8px;
`

const RowStatus = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: var(--text-11);
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 8px;
  width: fit-content;
`

const ErrorBlock = styled.div`
  display: grid;
  gap: 8px;
  justify-items: start;
`

const RetryButton = styled(Button)`
  ${css`
    color: var(--text-11);
  `}
`

const SkeletonList = styled.div`
  display: grid;
  gap: 4px;
  max-height: 280px;
`

const PairsFadeOverflow = styled(FadeOverflow)`
  display: grid;
  gap: 4px;
  max-height: 280px;
  /* height: 280px; */
  overflow-y: auto;
  overscroll-behavior: contain;
  align-content: start;
`
